/**
 * @file DocumentLayout.tsx
 * @description Каркас страницы документа (phase19.24 этап 1, шаг 1;
 * admin-design-system.md §5.1).
 *
 * Один каркас на приёмку, списание, перемещение и сборку: ссылка «назад» →
 * шапка → полосы → позиции слева и панель действия справа → история → sticky
 * действие на `<lg`. До него каждая из четырёх страниц собирала ту же сетку
 * руками, и `lg:grid-cols-[1fr_360px]` писался в трёх разных начертаниях.
 *
 * Полоса действия — `position: sticky` ПОСЛЕ сетки, а не `fixed` поверх неё:
 * на короткой странице (лист размещения без строк) fixed-кнопка накрывала
 * собой последний блок — под ней прятались «ПОЗИЦИИ (0)» и полоса-пояснение
 * (дефект QA 2026-09-20). Sticky на длинной странице ведёт себя как прежде
 * (прилипает к низу экрана), а на короткой встаёт в конец потока. Поэтому
 * бар живёт во внешнем контейнере-колонке: у грид-элемента блок-контейнер —
 * его же ячейка, и прилипать ему было бы некуда. По той же причине ушёл
 * нижний отступ `pb-28` — место под бар теперь занимает сам бар.
 *
 * Панель (`aside`) рендерится ТОЛЬКО если передана: закрытый документ читаем
 * во всю ширину, редактировать в нём нечего (правило §5.2). Без панели сетка
 * становится одноколоночной сама — вызывающему не нужно знать про grid.
 *
 * Порядок блоков задаёт ОДНА сетка через `order-*`, а не вложенные контейнеры:
 *   - на `<lg`: назад → шапка с плитками → ПАНЕЛЬ → полосы → позиции → история;
 *   - на `lg`: назад → шапка → полосы → позиции | панель → история.
 * Панель на телефоне стоит сразу под плитками, выше полос-уведомлений: поле
 * скана — первое, что видит кладовщик. Раньше длинный комментарий и полосы
 * уводили свёрнутую панель под fixed-кнопку «Провести» — на 360px «Подбор» и
 * скан были не видны и не нажимались без ручного скролла (дефект повторного
 * QA 2026-09-20). Двух экземпляров поля быть не может — они забирают друг у
 * друга фокус (урок сборки phase19.24), поэтому именно `order`, а не вторая
 * копия панели.
 *
 * Ширина панели ступенькой: 320px на `lg` и 360px на `xl`. На 1280px левой
 * колонке остаётся ровно 544px — под этот размер посчитаны колонки
 * `DocumentItemsTable`; на 1024px панель ужимается, иначе таблица позиций
 * начинала скроллиться уже на ноутбуке (замечание ревью 2026-09-20).
 */

import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface Props {
  /** `WmsBackLink` — «К приёмкам», «К очереди». */
  backLink?: ReactNode;
  /** `WmsDocumentHeader`: крошка, номер, статус, подзаголовок, плитки. */
  header: ReactNode;
  /** `NoticeStrip[]` под плитками (на `<lg` — под панелью). */
  notices?: ReactNode;
  /** Левая колонка: подпись «ПОЗИЦИИ (N)» и таблица/карточки. */
  main: ReactNode;
  /** Правая колонка 320/360px (`DocumentAside`) — только у живого документа. */
  aside?: ReactNode;
  /** «Движения по документу» / «История задания». */
  footer?: ReactNode;
  /** Главное действие на `<lg`: sticky-полоса снизу (правило warehouse-UI). */
  stickyBar?: ReactNode;
  /** Диалоги страницы — вне сетки, чтобы не участвовать в раскладке. */
  dialogs?: ReactNode;
  /** Дополнительные классы внешней колонки (редко нужны). */
  className?: string;
}

export function DocumentLayout({
  backLink,
  header,
  notices,
  main,
  aside,
  footer,
  stickyBar,
  dialogs,
  className,
}: Props) {
  return (
    // Внешняя колонка: сетка документа + sticky-полоса действия после неё.
    // `gap-4` разделяет их только на `<lg` — на `lg` полоса `display:none` и
    // флекс-зазора не создаёт.
    <div className={cn("flex flex-col gap-4", className)}>
      <div
        className={cn(
          // Страница лежит прямо на фоне, без белой карточки-обёртки (phase19.31, SC55).
          "grid grid-cols-1 gap-4 lg:gap-6",
          aside
            ? "lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px]"
            : "lg:grid-cols-1",
        )}
      >
        {backLink ? <div className="order-1 lg:col-span-full">{backLink}</div> : null}
        <div className="order-2 lg:col-span-full">{header}</div>

        {/* Панель — третьей на телефоне и последней на desktop (правая колонка). */}
        {aside ? (
          <div className="order-3 lg:order-5 lg:col-start-2">{aside}</div>
        ) : null}

        {/* `empty:hidden`: компонент полос может не отрисовать ни одной, и пустая
            ячейка сетки добавляла бы лишний зазор между шапкой и позициями. */}
        {notices ? (
          <div className="order-4 flex flex-col gap-4 empty:hidden lg:order-3 lg:col-span-full lg:gap-6">
            {notices}
          </div>
        ) : null}

        <div className="order-5 min-w-0 lg:order-4 lg:col-start-1">{main}</div>

        {footer ? (
          <div className="order-6 empty:hidden lg:order-6 lg:col-span-full">{footer}</div>
        ) : null}
      </div>

      {/* Полоса действия — последняя в потоке страницы (см. шапку файла).
          Диалоги идут за ней: они рисуются порталом и раскладку не трогают. */}
      {stickyBar}
      {dialogs}
    </div>
  );
}
